import { useCallback, useEffect, useState } from 'react';
import { verticalPricingApi, type VerticalCostsResponse } from '@/api/verticalPricing';

// Per-vertical Compute & Costs (VERTICAL-PRICING-ADMIN-1, Phase 2). Revenue/
// liability in (subscription + promo grants) vs compute out by sink (LLM, video
// render, …), outstanding balance, and per-tenant + daily breakdowns — sourced
// from the vertical's credit ledger. Distinct from the Director engine-cost
// rollup (CostsTab): this is one vertical's economics, operator-scoped.

const SINK_LABELS: Record<string, string> = {
  llm_usage: 'LLM usage',
  video_render: 'Video render',
  expiration: 'Expired allowance',
  marketplace_purchase: 'Marketplace purchase',
  marketplace_sale: 'Marketplace sale',
  admin_adjustment: 'Admin adjustment',
};
const REVENUE_LABELS: Record<string, string> = {
  subscription_grant: 'Subscription',
  purchase: 'Credit purchase',
  promotion: 'Promotional grant',
  share_reward: 'Share reward',
  admin_adjustment: 'Admin adjustment',
};

function label(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}

function money(value: number): string {
  return `$${value.toFixed(2)}`;
}

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(error.message) as { detail?: { message?: string } | string };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  } catch {
    /* plain body string */
  }
  return error.message;
}

function StatCard({ label: lbl, value, hint, accent }: { label: string; value: string; hint?: string; accent?: string }) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{lbl}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ?? 'text-gray-100'}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function RevenueVsComputeBars({ daily }: { daily: VerticalCostsResponse['daily'] }) {
  if (daily.length === 0) return <p className="text-gray-500 text-xs">No activity in this window.</p>;
  const max = Math.max(...daily.map((d) => Math.max(d.revenue_usd, d.compute_usd)), 0.01);
  return (
    <div className="flex items-end gap-1 h-24">
      {daily.slice(-60).map((d) => (
        <div key={d.date} className="flex-1 flex items-end gap-px" title={`${d.date}: +${money(d.revenue_usd)} / -${money(d.compute_usd)}`}>
          <div
            className="flex-1 bg-[#39FF14]/40 rounded-t"
            style={{ height: `${Math.max(2, (d.revenue_usd / max) * 100)}%`, minWidth: 2 }}
          />
          <div
            className="flex-1 bg-orange-400/50 rounded-t"
            style={{ height: `${Math.max(2, (d.compute_usd / max) * 100)}%`, minWidth: 2 }}
          />
        </div>
      ))}
    </div>
  );
}

export function VerticalCostsTab({ vertical }: { vertical: string }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<VerticalCostsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await verticalPricingApi.getCosts(vertical, days);
      setData(resp);
    } catch (err) {
      if (err instanceof Error && err.message.includes('403')) {
        setAccessError(`You are not authorized to administer the ${vertical} vertical.`);
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [vertical, days]);

  useEffect(() => {
    setAccessError(null);
    void load();
  }, [load]);

  if (accessError) {
    return (
      <div className="p-6 font-mono text-sm text-gray-400 space-y-3">
        <div className="text-xs uppercase tracking-wider text-gray-600">Compute & Costs</div>
        <div className="border border-red-500/30 bg-red-500/5 text-red-300 rounded p-3">{accessError}</div>
      </div>
    );
  }

  return (
    <div className="p-6 font-mono text-sm text-gray-200">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Compute & Costs</div>
          <div className="text-[11px] text-gray-600 mt-1">
            Revenue/liability in (grants) vs compute out (LLM, render, research) for this vertical.
          </div>
        </div>
        <label className="text-xs text-gray-500">
          Window{' '}
          <select
            aria-label="Window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ml-1 bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
      </div>

      {loading && !data ? (
        <p className="text-gray-500">Loading cost data…</p>
      ) : error && !data ? (
        <div className="text-red-400 text-xs">
          <p>Failed to load {vertical} costs.</p>
          <p className="text-gray-500 mt-1">{error}</p>
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-3">
            <StatCard label="Revenue in" value={money(data.revenue_in_usd)} hint={`+ ${money(data.promo_in_usd)} promo`} accent="text-[#39FF14]" />
            <StatCard label="Compute out" value={money(data.compute_out_usd)} hint={`${money(data.expiration_usd)} expired`} accent="text-orange-300" />
            <StatCard label="Net (granted − spend)" value={money(data.net_usd)} hint={`${money(data.total_spend_usd)} total spend`} />
            <StatCard label="Outstanding balance" value={money(data.outstanding_balance_usd)} hint="credits we still owe" />
          </div>

          <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Revenue vs compute · daily</div>
              <div className="text-[10px] text-gray-600">
                <span className="text-[#39FF14]">▰</span> revenue{'  '}
                <span className="text-orange-300">▰</span> compute
              </div>
            </div>
            <RevenueVsComputeBars daily={data.daily} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Compute out · by sink</div>
              {data.by_sink.length === 0 ? (
                <p className="text-gray-500 text-xs">No spend yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {data.by_sink.map((row) => (
                      <tr key={row.sink} className="border-t border-gray-800 first:border-t-0">
                        <td className="py-2 text-gray-300">{label(SINK_LABELS, row.sink)}</td>
                        <td className="py-2 text-right text-gray-200">{money(row.usd)}</td>
                        <td className="py-2 text-right text-gray-600">{row.count}×</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Granted in · by type</div>
              {data.by_revenue_type.length === 0 ? (
                <p className="text-gray-500 text-xs">No grants yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {data.by_revenue_type.map((row) => (
                      <tr key={row.type} className="border-t border-gray-800 first:border-t-0">
                        <td className="py-2 text-gray-300">{label(REVENUE_LABELS, row.type)}</td>
                        <td className="py-2 text-right text-gray-200">{money(row.usd)}</td>
                        <td className="py-2 text-right text-gray-600">{row.count}×</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">By tenant</div>
            {data.by_tenant.length === 0 ? (
              <p className="text-gray-500 text-xs">No tenants with ledger activity.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-1 font-normal">Tenant</th>
                    <th className="text-right py-1 font-normal">Granted</th>
                    <th className="text-right py-1 font-normal">Spent</th>
                    <th className="text-right py-1 font-normal">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_tenant.map((row) => (
                    <tr key={row.tenant_id} className="border-t border-gray-800">
                      <td className="py-2 text-gray-300 break-all">{row.tenant_id}</td>
                      <td className="py-2 text-right text-[#39FF14]/80">{money(row.granted_usd)}</td>
                      <td className="py-2 text-right text-orange-300/80">{money(row.spent_usd)}</td>
                      <td className="py-2 text-right text-gray-200">{money(row.balance_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
